/**
 * AI Chart Block
 * Renders a ```chart fenced block (JSON spec emitted by the assistant) with recharts.
 *
 * Spec: { type: "bar" | "line" | "area" | "pie", title?, xLabel?, yLabel?,
 *         data: [{ label: string, <series>: number, ... }] }
 */

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// App chart tokens (brand indigo ramp), ordered for maximum adjacent separation.
const SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-3)',
  'var(--chart-2)',
  'var(--chart-5)',
  'var(--chart-4)',
];

interface ChartSpec {
  type: 'bar' | 'line' | 'area' | 'pie';
  title?: string;
  xLabel?: string;
  yLabel?: string;
  data: Array<Record<string, unknown>>;
}

function parseSpec(source: string): ChartSpec | null {
  try {
    const spec = JSON.parse(source);
    if (
      spec &&
      ['bar', 'line', 'area', 'pie'].includes(spec.type) &&
      Array.isArray(spec.data) &&
      spec.data.length > 0
    ) {
      return spec as ChartSpec;
    }
  } catch {
    // not valid JSON (yet) — fall through
  }
  return null;
}

const tooltipStyle = {
  backgroundColor: 'var(--popover)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--popover-foreground)',
};

export function AIChartBlock({ source }: { source: string }) {
  const spec = parseSpec(source);

  if (!spec) {
    return (
      <pre className="overflow-x-auto rounded-md bg-background/50 p-2 text-xs">{source}</pre>
    );
  }

  const seriesKeys = Object.keys(spec.data[0]).filter(
    (k) => k !== 'label' && spec.data.some((row) => typeof row[k] === 'number'),
  );

  if (seriesKeys.length === 0) {
    return null;
  }

  // Pie only reads well with few slices; beyond 5 fall back to a bar chart.
  const type = spec.type === 'pie' && spec.data.length > SERIES_COLORS.length ? 'bar' : spec.type;
  const showLegend = seriesKeys.length > 1;

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
      <XAxis
        dataKey="label"
        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
        tickLine={false}
        axisLine={{ stroke: 'var(--border)' }}
      />
      <YAxis
        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
        tickLine={false}
        axisLine={false}
        width={48}
      />
      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
      {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
    </>
  );

  let chart: React.ReactElement;
  if (type === 'pie') {
    const key = seriesKeys[0];
    chart = (
      <PieChart>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Pie
          data={spec.data}
          dataKey={key}
          nameKey="label"
          innerRadius="55%"
          outerRadius="85%"
          paddingAngle={2}
          stroke="var(--card)"
          strokeWidth={2}
        >
          {spec.data.map((_, i) => (
            <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    );
  } else if (type === 'line') {
    chart = (
      <LineChart data={spec.data}>
        {axes}
        {seriesKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    );
  } else if (type === 'area') {
    chart = (
      <AreaChart data={spec.data}>
        {axes}
        {seriesKeys.map((key, i) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            fillOpacity={0.15}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    );
  } else {
    chart = (
      <BarChart data={spec.data} barCategoryGap="25%">
        {axes}
        {seriesKeys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          />
        ))}
      </BarChart>
    );
  }

  return (
    <div className="not-prose my-3 rounded-xl bg-card p-3">
      {spec.title && <p className="mb-2 text-sm font-medium">{spec.title}</p>}
      <ResponsiveContainer width="100%" height={260}>
        {chart}
      </ResponsiveContainer>
      {(spec.xLabel || spec.yLabel) && (
        <p className="mt-1 text-center text-[11px] text-muted-foreground">
          {[spec.yLabel, spec.xLabel].filter(Boolean).join(' by ')}
        </p>
      )}
    </div>
  );
}
